import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ReceiptText, Files, Landmark, HandCoins, BookOpen,
  BarChart3, Users, LogOut, Truck, Settings as SettingsIcon
} from "lucide-react";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/new-receipt", label: "New Money Receipt", icon: ReceiptText, testid: "nav-new-receipt" },
  { to: "/receipts", label: "Receipt Register", icon: Files, testid: "nav-receipts" },
  { to: "/deposit", label: "Deposit to Bank", icon: Landmark, testid: "nav-deposit" },
  { to: "/handover", label: "Owner Handover", icon: HandCoins, testid: "nav-handover" },
  { to: "/ledger", label: "Cash Ledger", icon: BookOpen, testid: "nav-ledger" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 sidebar-gradient text-white flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-xl bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src="/logo_transparent.png" alt="Ratan Motors" className="h-12 w-12 object-contain" />
            </div>
            <div className="min-w-0">
              <div className="font-extrabold tracking-tight text-lg leading-none">Ratan Motors</div>
              <div className="text-[11px] text-blue-200 mt-1.5">Central Cash Mgmt</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `link-row flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${isActive ? "active" : "text-blue-100"}`
              }
            >
              <it.icon className="h-4.5 w-4.5" />
              <span>{it.label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/users"
              data-testid="nav-users"
              className={({ isActive }) =>
                `link-row flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${isActive ? "active" : "text-blue-100"}`
              }
            >
              <Users className="h-4 w-4" />
              <span>User Management</span>
            </NavLink>
          )}
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `link-row flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${isActive ? "active" : "text-blue-100"}`
            }
          >
            <SettingsIcon className="h-4 w-4" />
            <span>Company Settings</span>
          </NavLink>
        </nav>

        <div className="p-4 border-t border-white/10 text-xs text-blue-200">
          Tata Motors CV Dealer<br />Jharkhand — 7 Branches
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Head Office</div>
            <div className="font-bold text-slate-800 text-lg">Welcome, {user?.name?.split(" ")[0] || "User"}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold text-slate-700">
                {now.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
              </div>
              <div className="text-xs text-slate-400 mono">{now.toLocaleTimeString("en-IN")}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                {(user?.name?.[0] || "U").toUpperCase()}
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="text-[11px] uppercase text-slate-400 tracking-wider">{user?.role}</div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/login"); }}
              className="gap-1.5"
            >
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </header>

        <main className="p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
